import {
    PHARMACY_PATIENT_NOTIFICATIONS_REQUEST,
    PHARMACY_PATIENT_NOTIFICATIONS_SUCCESS,
    PHARMACY_PATIENT_NOTIFICATIONS_FAILURE,
    PHARMACY_PATIENT_NOTIFICATIONS_RESET,
    PHARMACY_PATIENT_NOTIFICATIONS_REFRESH_PAGE,
    PHARMACY_PATIENT_NOTIFICATIONS_UPDATE_NOTE
} from '../../../constants';

const initialState = {
    data: null,
    loading: false,
    error: null,
    type: null,
}

const pharmacyPatientNotificationsReducer = (state = initialState, action) => {
    switch (action.type) {
        case PHARMACY_PATIENT_NOTIFICATIONS_REQUEST:
            return {
                ...state,
                loading: true,
                error: null,
                type: action.types,
            };
        case PHARMACY_PATIENT_NOTIFICATIONS_SUCCESS:
            // If appendData flag is true and we have existing data, merge the arrays
            if (action.appendData && state.data?.data?.data) {
                const existingData = state.data?.data?.data || [];
                const newData = action.payload?.data?.data || [];
                return {
                    ...state,
                    loading: false,
                    data: {
                        ...action.payload,
                        data: {
                            ...action.payload?.data,
                            data: [...existingData, ...newData]
                        }
                    },
                    error: null,
                    type: action.types,
                };
            }
            return {
                ...state,
                loading: false,
                data: action.payload,
                error: null,
                type: action.types,
            };
        case PHARMACY_PATIENT_NOTIFICATIONS_REFRESH_PAGE:
            // Refresh data by finding and updating notes by note_id
            // This handles infinite scroll where notes could be anywhere in the merged list
            if (state.data?.data?.data) {
                const existingData = state.data?.data?.data || [];
                const refreshedPageData = action.payload?.data?.data || [];
                
                // Create a map of refreshed notes by note_id for quick lookup
                const refreshedNotesMap = new Map();
                refreshedPageData.forEach(item => {
                    refreshedNotesMap.set(item.note_id, item);
                });
                
                // Update existing items if they exist in refreshed data
                // Keep items that aren't in the refreshed page as-is
                const mergedData = existingData.map(existingItem => {
                    const refreshedItem = refreshedNotesMap.get(existingItem.note_id);
                    if (refreshedItem) {
                        // Found in refreshed data - use the updated version
                        return refreshedItem;
                    }
                    // Not in refreshed data - keep existing
                    return existingItem;
                });
                
                // Filter out any items that were deleted (existed before but not in refresh)
                // Only filter items that SHOULD be on the refreshed page
                const pageSize = 20;
                const currentPage = action.currentPage;
                const startIndex = (currentPage - 1) * pageSize;
                const endIndex = Math.min(startIndex + pageSize, mergedData.length);
                
                // Get note_ids from refreshed data
                const refreshedNoteIds = new Set(refreshedPageData.map(item => item.note_id));
                
                // Check if any items in the current page range were deleted
                const finalData = mergedData.filter((item, index) => {
                    // If item is in the current page range, it must exist in refreshed data
                    if (index >= startIndex && index < endIndex) {
                        return refreshedNoteIds.has(item.note_id);
                    }
                    // Items outside current page range are kept
                    return true;
                });
                
                return {
                    ...state,
                    loading: false,
                    data: {
                        ...state.data,
                        data: {
                            ...state.data?.data,
                            data: finalData,
                            pagination: action.payload?.data?.pagination || state.data?.data?.pagination
                        }
                    },
                    error: null,
                    type: action.types,
                };
            }
            return {
                ...state,
                loading: false,
                data: action.payload,
                error: null,
                type: action.types,
            };
        case PHARMACY_PATIENT_NOTIFICATIONS_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.payload,
                type: action.types,
            };
        case PHARMACY_PATIENT_NOTIFICATIONS_RESET:
            return initialState;
        case PHARMACY_PATIENT_NOTIFICATIONS_UPDATE_NOTE:
            // Update a specific note in the list (e.g., add reply, update pin status, delete note, etc.)
            if (state.data?.data?.data) {
                const existingData = state.data?.data?.data || [];
                const { noteId, updateData, updateType } = action.payload;
                
                // Handle delete - filter out the note
                if (updateType === 'deleteNote') {
                    const filteredData = existingData.filter(item => item.note_id !== noteId);
                    return {
                        ...state,
                        data: {
                            ...state.data,
                            data: {
                                ...state.data?.data,
                                data: filteredData
                            }
                        }
                    };
                }
                
                const updatedData = existingData.map(item => {
                    if (item.note_id === noteId) {
                        if (updateType === 'addReply') {
                            // Add new reply to the note's reply array
                            return {
                                ...item,
                                reply: [...(item.reply || []), updateData]
                            };
                        } else if (updateType === 'updateNote') {
                            // Merge updated fields into the note
                            return { ...item, ...updateData };
                        }
                    }
                    return item;
                });
                
                return {
                    ...state,
                    data: {
                        ...state.data,
                        data: {
                            ...state.data?.data,
                            data: updatedData
                        }
                    }
                };
            }
            return state;
        default:
            return state;
    }
};

export default pharmacyPatientNotificationsReducer;
